import React, { useCallback, useState } from 'react';
import { Button, Text } from 'src/@vymo/ui/atoms';
import type { Option as SelectOption } from 'src/@vymo/ui/atoms/select/types';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  DEFAULT_PLAYBOOK_TRACKER_ENV_URL,
  INITIAL_FILTERS,
  PAGE_SIZE,
  toSearchFilters,
} from './constants';
import PlaybookTrackerResultRow from './detailsCard';
import PlaybookTrackerFiltersBar from './filters';
import {
  getPlaybookTrackerLoading,
  getPlaybookTrackerRows,
  getPlaybookTrackerTotal,
} from './selector';
import { searchPlaybookActions } from './thunk';
import { PlaybookTrackerFilters, PlaybookTrackerSelectKey } from './types';
import styles from './index.module.scss';

function PlaybookTracker() {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(getPlaybookTrackerLoading);
  const rows = useAppSelector(getPlaybookTrackerRows);
  const total = useAppSelector(getPlaybookTrackerTotal);
  const [filters, setFilters] =
    useState<PlaybookTrackerFilters>(INITIAL_FILTERS);
  const [from, setFrom] = useState(0);
  const [customEnvUrl, setCustomEnvUrl] = useState(
    DEFAULT_PLAYBOOK_TRACKER_ENV_URL,
  );

  const fetchPage = useCallback(
    (nextFilters: PlaybookTrackerFilters, nextFrom: number) => {
      setFrom(nextFrom);
      dispatch(
        searchPlaybookActions(toSearchFilters(nextFilters), nextFrom, false),
      );
    },
    [dispatch],
  );

  const runSearch = useCallback(
    (nextFilters: PlaybookTrackerFilters) => {
      fetchPage(nextFilters, 0);
    },
    [fetchPage],
  );

  const onInputChange = useCallback(
    (key: PlaybookTrackerSelectKey) => (value: string) => {
      setFilters((current) => ({
        ...current,
        [key]: value,
      }));
    },
    [],
  );

  const handleEnvSelect = useCallback((name: string, url: string) => {
    setFilters((current) => ({
      ...current,
      env: url,
      envLabel: name,
    }));
    setCustomEnvUrl(url);
  }, []);

  const onStatusChange = useCallback((values: SelectOption[]) => {
    setFilters((current) => ({
      ...current,
      status: values[0]?.value || '',
    }));
  }, []);

  const clearStatusFilter = useCallback(() => {
    setFilters((current) => ({
      ...current,
      status: '',
    }));
  }, []);

  const onStatusClearClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      clearStatusFilter();
    },
    [clearStatusFilter],
  );

  const handlePreviousPage = useCallback(() => {
    if (from === 0) {
      return;
    }

    fetchPage(filters, Math.max(from - PAGE_SIZE, 0));
  }, [fetchPage, filters, from]);

  const handleNextPage = useCallback(() => {
    if (from + PAGE_SIZE >= total) {
      return;
    }

    fetchPage(filters, from + PAGE_SIZE);
  }, [fetchPage, filters, from, total]);

  const clearFilters = useCallback(() => {
    setFrom(0);
    setFilters(INITIAL_FILTERS);
    setCustomEnvUrl(DEFAULT_PLAYBOOK_TRACKER_ENV_URL);
  }, []);

  const onDateRangeChange = useCallback((value: [Date, Date]) => {
    setFilters((current) => ({
      ...current,
      dateRange: value,
    }));
  }, []);

  const handleCustomEnvAdd = useCallback(() => {
    const trimmedUrl = customEnvUrl.trim();

    if (!trimmedUrl) {
      return;
    }

    setFilters((current) => ({
      ...current,
      env: trimmedUrl,
      envLabel: trimmedUrl,
    }));
  }, [customEnvUrl]);

  const currentPage = Math.floor(from / PAGE_SIZE) + 1;
  const totalPages = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0;
  const startResult = total === 0 ? 0 : from + 1;
  const endResult = total === 0 ? 0 : from + rows.length;
  const canGoPrevious = from > 0 && !loading;
  const canGoNext = from + PAGE_SIZE < total && !loading;

  return (
    <div className={styles.playbookTracker}>
      <div className={styles.playbookTracker__titleRow}>
        <div>
          <Text type="h3">Playbook Tracker</Text>
        </div>
        <Text type="h6">
          Showing {startResult}-{endResult} of {total} results
        </Text>
      </div>

      <PlaybookTrackerFiltersBar
        filters={filters}
        loading={loading}
        customEnvUrl={customEnvUrl}
        onInputChange={onInputChange}
        onStatusChange={onStatusChange}
        onStatusClearClick={onStatusClearClick}
        onDateRangeChange={onDateRangeChange}
        onCustomEnvUrlChange={setCustomEnvUrl}
        onCustomEnvAdd={handleCustomEnvAdd}
        onEnvSelect={handleEnvSelect}
        onSearch={() => runSearch(filters)}
        onReset={clearFilters}
      />
      <div className={styles.playbookTracker__results}>
        {rows.length === 0 ? (
          <Text>No results found</Text>
        ) : (
          rows.map((row) => <PlaybookTrackerResultRow row={row} />)
        )}
      </div>
      {total > 0 && (
        <div className={styles.playbookTracker__pagination}>
          <Button onClick={handlePreviousPage} disabled={!canGoPrevious}>
            Previous
          </Button>
          <Text type="label">
            Page {currentPage}
            {totalPages > 0 ? ` of ${totalPages}` : ''}
          </Text>
          <Button onClick={handleNextPage} disabled={!canGoNext}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

export default PlaybookTracker;
